/**
 * Backup e diagnóstico incluindo IndexedDB (lotes, tempo de corte, histórico, etc.).
 * O backup antigo só exportava localStorage — lotes ficavam só no IDB e sumiam do .json.
 */

import { extrairNumeroLote, resumoLotes, type LoteProdutoPersistido } from './adePersistenciaLotes';

const DB_TEMPO_CORTE = 'ade-tempo-corte-db';
const DB_TEMPO_CORTE_VERSION = 9;
const DB_PRODUTO_PALETE = 'ade-produto-palete-db';

const STORES_TEMPO_CORTE: { store: string; key: string }[] = [
  { store: 'lotesProdutos', key: 'lotesProdutos' },
  { store: 'tempoCorte', key: 'produtosTempoCorteSalvos' },
  { store: 'tempoCorte', key: 'tempoCorteItens' },
  { store: 'tempoCorte', key: 'lotesProdutos' },
  { store: 'historicoImpressoes', key: 'historicoImpressoes' },
  { store: 'sobraPecas', key: 'sobraPecas' },
  { store: 'sobraRiscados', key: 'sobraRiscados' },
  { store: 'sobraPastas', key: 'sobraPastas' },
  { store: 'relatorioPecas', key: 'relatorioPecas' },
  { store: 'layoutImpressao', key: 'layoutImpressao' },
];

const LS_CHAVES_CRITICAS = [
  'lotes-produtos-ade',
  'produtos-tempo-corte-ade',
] as const;

function abrirDb(nome: string, versao: number): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(nome, versao);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Abre o banco garantindo que as object stores existam (cria via upgrade se faltar).
 * Sem isso, restaurar backup em perfil “novo” silenciosamente não gravava nada.
 */
function abrirDbComStores(nome: string, storesNecessarios: string[]): Promise<IDBDatabase> {
  const unicos = [...new Set(storesNecessarios.filter(Boolean))];
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(nome);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => {
      const db = req.result;
      const faltando = unicos.filter((s) => !db.objectStoreNames.contains(s));
      if (faltando.length === 0) {
        resolve(db);
        return;
      }
      const nextVer = db.version + 1;
      db.close();
      const req2 = indexedDB.open(nome, nextVer);
      req2.onerror = () => reject(req2.error);
      req2.onupgradeneeded = () => {
        const d2 = req2.result;
        for (const s of faltando) {
          if (!d2.objectStoreNames.contains(s)) d2.createObjectStore(s);
        }
      };
      req2.onsuccess = () => resolve(req2.result);
    };
  });
}

async function lerChaveDb(db: IDBDatabase, store: string, key: string): Promise<unknown> {
  if (!db.objectStoreNames.contains(store)) return null;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).get(key);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

async function gravarChaveDb(db: IDBDatabase, store: string, key: string, valor: unknown): Promise<void> {
  if (!db.objectStoreNames.contains(store)) return;
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.objectStore(store).put(valor, key);
  });
}

function parseJsonArray(raw: string | null): unknown[] {
  if (!raw) return [];
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}

function contarJun11(lista: LoteProdutoPersistido[]): number {
  return lista.filter(
    (x) =>
      String(x.dataHoraInicioCorte || '').includes('2026-06-11') ||
      String(x.dataHoraFimEmbalagem || '').includes('2026-06-11')
  ).length;
}

export interface DiagnosticoFonteDados {
  fonte: string;
  lotes: number;
  maiorLote: number;
  jun11: number;
  produtosTempoCorte: number;
}

export interface DiagnosticoCompleto {
  data: string;
  fontes: DiagnosticoFonteDados[];
  melhorMaiorLote: number;
  temDadosApos8729: boolean;
  temJun11: boolean;
  textoResumo: string;
}

export async function diagnosticarTodasFontesDados(): Promise<DiagnosticoCompleto> {
  const fontes: DiagnosticoFonteDados[] = [];

  for (const chave of LS_CHAVES_CRITICAS) {
    const arr = parseJsonArray(localStorage.getItem(chave)) as LoteProdutoPersistido[];
    if (chave === 'lotes-produtos-ade') {
      const r = resumoLotes(arr);
      fontes.push({
        fonte: `localStorage:${chave}`,
        lotes: r.total,
        maiorLote: r.maiorNumeroLote,
        jun11: contarJun11(arr),
        produtosTempoCorte: 0,
      });
    }
    if (chave === 'produtos-tempo-corte-ade') {
      fontes.push({
        fonte: `localStorage:${chave}`,
        lotes: 0,
        maiorLote: 0,
        jun11: 0,
        produtosTempoCorte: arr.length,
      });
    }
  }

  try {
    const db = await abrirDb(DB_TEMPO_CORTE, DB_TEMPO_CORTE_VERSION);
    for (const { store, key } of STORES_TEMPO_CORTE) {
      const data = await lerChaveDb(db, store, key);
      if (!Array.isArray(data) || data.length === 0) continue;
      if (key === 'lotesProdutos') {
        const r = resumoLotes(data as LoteProdutoPersistido[]);
        fontes.push({
          fonte: `IndexedDB:${store}/${key}`,
          lotes: r.total,
          maiorLote: r.maiorNumeroLote,
          jun11: contarJun11(data as LoteProdutoPersistido[]),
          produtosTempoCorte: 0,
        });
      }
      if (key === 'produtosTempoCorteSalvos') {
        fontes.push({
          fonte: `IndexedDB:${store}/${key}`,
          lotes: 0,
          maiorLote: 0,
          jun11: 0,
          produtosTempoCorte: data.length,
        });
      }
    }
    db.close();
  } catch {
    /* IDB indisponível */
  }

  const melhorMaiorLote = Math.max(0, ...fontes.map((f) => f.maiorLote));
  const temJun11 = fontes.some((f) => f.jun11 > 0);
  const temDadosApos8729 = melhorMaiorLote > 8729;

  let textoResumo: string;
  if (temDadosApos8729) {
    textoResumo = `Encontrados lotes até nº ${melhorMaiorLote} em alguma fonte. Use "Recuperar do backup" nas abas ou restaure um .json completo.`;
  } else if (melhorMaiorLote > 0) {
    textoResumo = `Nenhuma fonte tem lote acima de 8729. Maior encontrado: ${melhorMaiorLote}. Lotes cadastrados após 09/06 (ex.: 11/06 até 8808) não estão neste PC — só em backup externo ou outro computador.`;
  } else {
    textoResumo = 'Nenhum lote encontrado nas fontes atuais.';
  }

  return {
    data: new Date().toISOString(),
    fontes,
    melhorMaiorLote,
    temDadosApos8729,
    temJun11,
    textoResumo,
  };
}

const DBS_FALLBACK: { name: string; version: number }[] = [
  { name: DB_TEMPO_CORTE, version: DB_TEMPO_CORTE_VERSION },
  { name: DB_PRODUTO_PALETE, version: 1 },
  { name: 'ade-copa-db', version: 1 },
  { name: 'ade-tempo-embalar-db', version: 1 },
  { name: 'ade-tempo-producao-peca-db', version: 1 },
  { name: 'ade-calc-planilha-db', version: 1 },
  { name: 'ade-adiantamento-mdf-db', version: 1 },
];

function chaveIdbPath(dbName: string, store: string, key: IDBValidKey): string {
  return `${dbName}/${store}/${encodeURIComponent(String(key))}`;
}

function parseChaveIdbPath(pathKey: string): { dbName: string; store: string; key: string } | null {
  const parts = pathKey.split('/');
  if (parts.length < 3) return null;
  const dbName = parts[0];
  const store = parts[1];
  const key = decodeURIComponent(parts.slice(2).join('/'));
  return { dbName, store, key };
}

async function listarBancosIndexedDB(): Promise<{ name: string; version: number }[]> {
  const listFn = (
    indexedDB as IDBFactory & {
      databases?: () => Promise<Array<{ name?: string; version?: number }>>;
    }
  ).databases;
  if (listFn) {
    try {
      const raw = await listFn.call(indexedDB);
      const list = raw
        .filter((d) => d.name && String(d.name).startsWith('ade-'))
        .map((d) => ({ name: d.name!, version: d.version ?? 1 }));
      if (list.length > 0) return list;
    } catch {
      /* fallback */
    }
  }
  return DBS_FALLBACK;
}

async function lerTodasEntradasStore(
  db: IDBDatabase,
  storeName: string
): Promise<Array<{ key: IDBValidKey; value: unknown }>> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const reqKeys = store.getAllKeys();
    reqKeys.onsuccess = () => {
      const keys = reqKeys.result ?? [];
      if (keys.length === 0) {
        resolve([]);
        return;
      }
      const reqVals = store.getAll();
      reqVals.onsuccess = () => {
        const vals = reqVals.result ?? [];
        resolve(keys.map((key, i) => ({ key, value: vals[i] })));
      };
      reqVals.onerror = () => reject(reqVals.error);
    };
    reqKeys.onerror = () => reject(reqKeys.error);
  });
}

/** Exporta TODAS as entradas de todos os bancos ade-* (lotes, catálogo, copa, palete…). */
export async function exportarTodoIndexedDB(): Promise<Record<string, unknown>> {
  const out: Record<string, unknown> = {};
  const bancos = await listarBancosIndexedDB();
  for (const { name, version } of bancos) {
    try {
      const db = await abrirDb(name, version);
      for (const storeName of Array.from(db.objectStoreNames)) {
        const entradas = await lerTodasEntradasStore(db, storeName);
        for (const { key, value } of entradas) {
          if (value !== undefined) {
            out[chaveIdbPath(name, storeName, key)] = value;
          }
        }
      }
      db.close();
    } catch (err) {
      console.warn('[Sistema Ade] Backup: falha ao ler', name, err);
    }
  }
  return out;
}

export interface BackupCompletoPayload {
  _tipo: 'backup-ade';
  _versao: string;
  _data: string;
  _incluiIndexedDB: true;
  _exportacaoCompleta?: boolean;
  dados: Record<string, string>;
  indexedDB: Record<string, unknown>;
}

export async function montarBackupCompleto(versaoApp = ''): Promise<BackupCompletoPayload> {
  const dados: Record<string, string> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k) dados[k] = localStorage.getItem(k) ?? '';
  }

  const indexedDBData = await exportarTodoIndexedDB();

  return {
    _tipo: 'backup-ade',
    _versao: versaoApp,
    _data: new Date().toISOString(),
    _incluiIndexedDB: true,
    _exportacaoCompleta: true,
    dados,
    indexedDB: indexedDBData,
  };
}

export function baixarJsonBackup(payload: BackupCompletoPayload, nomeArq: string): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArq;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export async function restaurarBackupCompleto(
  texto: string
): Promise<{ ok: boolean; chaves: number; chavesIdb: number; erro?: string }> {
  try {
    const parsed = JSON.parse(texto) as BackupCompletoPayload & { dados?: Record<string, unknown> };
    let dados: Record<string, unknown> | null = null;
    if (parsed?.dados && typeof parsed.dados === 'object') dados = parsed.dados;
    else if (parsed && typeof parsed === 'object') dados = parsed as Record<string, unknown>;

    if (!dados) return { ok: false, chaves: 0, chavesIdb: 0, erro: 'Arquivo não reconhecido.' };

    let n = 0;
    Object.keys(dados).forEach((k) => {
      if (k.startsWith('_')) return;
      const v = dados![k];
      if (typeof v === 'string') {
        localStorage.setItem(k, v);
        n += 1;
      }
    });

    let chavesIdb = 0;
    const idbMap = parsed.indexedDB;
    if (idbMap && typeof idbMap === 'object') {
      const porBanco = new Map<string, Array<{ store: string; key: string; valor: unknown }>>();
      for (const [pathKey, valor] of Object.entries(idbMap)) {
        const parsedPath = parseChaveIdbPath(pathKey);
        if (!parsedPath) continue;
        const lista = porBanco.get(parsedPath.dbName) ?? [];
        lista.push({ store: parsedPath.store, key: parsedPath.key, valor });
        porBanco.set(parsedPath.dbName, lista);
      }
      const bancos = await listarBancosIndexedDB();
      for (const [dbName, entradas] of porBanco) {
        const meta = bancos.find((b) => b.name === dbName) ?? { name: dbName, version: 1 };
        try {
          const stores = [...new Set(entradas.map((e) => e.store))];
          const db = await abrirDbComStores(meta.name, stores);
          for (const { store, key, valor } of entradas) {
            await gravarChaveDb(db, store, key, valor);
            chavesIdb += 1;
          }
          db.close();
        } catch {
          /* banco indisponível */
        }
      }
    }

    return { ok: true, chaves: n, chavesIdb };
  } catch {
    return { ok: false, chaves: 0, chavesIdb: 0, erro: 'Arquivo inválido (não é JSON).' };
  }
}

/** Restaura só as chaves informadas (backup por módulo — não apaga o resto do app). */
export async function restaurarEntradasBackupParcial(
  dados: Record<string, string>,
  idbMap: Record<string, unknown>
): Promise<{ chaves: number; chavesIdb: number }> {
  let n = 0;
  Object.entries(dados).forEach(([k, v]) => {
    if (k.startsWith('_')) return;
    if (typeof v === 'string') {
      try {
        localStorage.setItem(k, v);
        n += 1;
      } catch {
        /* quota */
      }
    }
  });

  let chavesIdb = 0;
  if (idbMap && typeof idbMap === 'object') {
    const porBanco = new Map<string, Array<{ store: string; key: string; valor: unknown }>>();
    for (const [pathKey, valor] of Object.entries(idbMap)) {
      const parsedPath = parseChaveIdbPath(pathKey);
      if (!parsedPath) continue;
      const lista = porBanco.get(parsedPath.dbName) ?? [];
      lista.push({ store: parsedPath.store, key: parsedPath.key, valor });
      porBanco.set(parsedPath.dbName, lista);
    }
    const bancos = await listarBancosIndexedDB();
    for (const [dbName, entradas] of porBanco) {
      const meta = bancos.find((b) => b.name === dbName) ?? { name: dbName, version: 1 };
      try {
        const stores = [...new Set(entradas.map((e) => e.store))];
        const db = await abrirDbComStores(meta.name, stores);
        for (const { store, key, valor } of entradas) {
          await gravarChaveDb(db, store, key, valor);
          chavesIdb += 1;
        }
        db.close();
      } catch {
        /* banco indisponível */
      }
    }
  }

  return { chaves: n, chavesIdb };
}

export function extrairLotesDeBackupJson(texto: string): LoteProdutoPersistido[] {
  try {
    const parsed = JSON.parse(texto) as BackupCompletoPayload;
    const listas: LoteProdutoPersistido[][] = [];
    const dados = parsed.dados || {};
    const raw = dados['lotes-produtos-ade'];
    if (typeof raw === 'string') listas.push(parseJsonArray(raw) as LoteProdutoPersistido[]);
    const idb = parsed.indexedDB || {};
    for (const [k, v] of Object.entries(idb)) {
      if (k.includes('lotesProdutos') && Array.isArray(v)) listas.push(v as LoteProdutoPersistido[]);
    }
    const map = new Map<string, LoteProdutoPersistido>();
    for (const lista of listas) {
      for (const lp of lista) {
        if (!lp?.lote) continue;
        const id = String(lp.id || '').trim();
        if (id) map.set(id, lp);
        else map.set(`${lp.lote}|${lp.produtoNome}`, lp);
      }
    }
    return [...map.values()].sort(
      (a, b) => extrairNumeroLote(a.lote) - extrairNumeroLote(b.lote)
    );
  } catch {
    return [];
  }
}
